import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import {
  Search,
  Filter,
  X,
  ChevronDown,
  RotateCcw,
} from 'lucide-react'
import type { CompanyFilterParams, CompanyFilterOptions } from '@/types'

interface CompanyFiltersProps {
  jobId: number
  filters: CompanyFilterParams
  onFiltersChange: (filters: CompanyFilterParams) => void
  filterOptions: CompanyFilterOptions | undefined
}

interface AccordionSectionProps {
  title: string
  subtitle?: string
  defaultOpen?: boolean
  children: React.ReactNode
}

function AccordionSection({ title, subtitle, defaultOpen = false, children }: AccordionSectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  const contentRef = useRef<HTMLDivElement>(null)
  const [maxH, setMaxH] = useState<number | undefined>(defaultOpen ? undefined : 0)

  useEffect(() => {
    if (open) {
      const el = contentRef.current
      if (el) {
        setMaxH(el.scrollHeight)
        const timer = setTimeout(() => setMaxH(undefined), 280)
        return () => clearTimeout(timer)
      }
    } else {
      setMaxH(0)
    }
  }, [open])

  return (
    <div style={{ borderBottom: '1px solid var(--border-subtle)' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '6px 10px',
          background: 'none',
          border: 'none',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 500,
          transition: 'color var(--duration-fast)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <ChevronDown
            size={12}
            style={{
              color: open ? 'var(--accent)' : 'var(--text-muted)',
              transform: open ? 'none' : 'rotate(-90deg)',
              transition: 'transform var(--duration-fast)',
            }}
          />
          {title}
          {subtitle && <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 11, marginLeft: 2 }}>{subtitle}</span>}
        </span>
      </button>
      <div
        ref={contentRef}
        style={{
          overflow: 'hidden',
          maxHeight: maxH,
          transition: maxH === 0 ? 'max-height 220ms ease-out' : 'max-height 260ms ease-in',
        }}
      >
        <div style={{ padding: '2px 10px 10px' }}>
          {children}
        </div>
      </div>
    </div>
  )
}

const defaultFilters: CompanyFilterParams = {
  search: undefined,
  entity_type: undefined,
  active_only: true,
  okved: undefined,
  region: undefined,
  date_from: undefined,
  date_to: undefined,
  capital_min: undefined,
  revenue_min: undefined,
  revenue_max: undefined,
  profit_min: undefined,
  profit_max: undefined,
  assets_min: undefined,
  assets_max: undefined,
  taxes_min: undefined,
  taxes_max: undefined,
  has_phone: undefined,
  has_email: undefined,
  has_website: undefined,
  has_licenses: undefined,
  has_trademarks: undefined,
  has_procurements: undefined,
  director: undefined,
}

const compactInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '5px 8px',
  fontSize: 12,
  background: 'var(--bg-input)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-xs)',
  color: 'var(--text-primary)',
  outline: 'none',
  transition: 'border-color var(--duration-fast)',
}

function NumericInput({ placeholder, value, onChange }: {
  placeholder: string
  value: number | undefined
  onChange: (v: number | undefined) => void
}) {
  return (
    <input
      type="number"
      style={compactInputStyle}
      value={value ?? ''}
      onChange={e => onChange(e.target.value ? Number(e.target.value) : undefined)}
      placeholder={placeholder}
    />
  )
}

function PillToggle({ label, active, onChange }: {
  label: string
  active: boolean | undefined
  onChange: (v: boolean | undefined) => void
}) {
  const isActive = active === true
  return (
    <button
      onClick={() => onChange(isActive ? undefined : true)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        fontSize: 11,
        fontWeight: 500,
        background: isActive ? 'var(--accent-subtle)' : 'transparent',
        border: `1px solid ${isActive ? 'var(--accent-border)' : 'var(--border-default)'}`,
        borderRadius: 'var(--radius-full)',
        color: isActive ? 'var(--accent-bright)' : 'var(--text-muted)',
        cursor: 'pointer',
        transition: 'all var(--duration-fast)',
        whiteSpace: 'nowrap' as const,
      }}
    >
      {label}
    </button>
  )
}

function OkvedAutocomplete({ value, onChange, options }: {
  value: string | undefined
  onChange: (v: string | undefined) => void
  options: { value: string; count: number }[]
}) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    if (!search) return options.slice(0, 50)
    return options.filter(o => o.value.toLowerCase().includes(search.toLowerCase())).slice(0, 50)
  }, [options, search])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        style={compactInputStyle}
        placeholder="Поиск по коду ОКВЭД..."
        value={value || search}
        onChange={e => { setSearch(e.target.value); onChange(e.target.value || undefined) }}
        onFocus={() => setOpen(true)}
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 50,
          maxHeight: 160,
          overflowY: 'auto',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-xs)',
          boxShadow: 'var(--shadow-md)',
          marginTop: 2,
        }}>
          {filtered.map(o => (
            <button
              key={o.value}
              onClick={() => {
                onChange(o.value)
                setSearch('')
                setOpen(false)
              }}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                padding: '5px 8px',
                fontSize: 11,
                background: 'transparent',
                border: 'none',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-subtle)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
            >
              <span>{o.value}</span>
              <span style={{ color: 'var(--text-muted)' }}>{o.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export { defaultFilters }

export default function CompanyFilters({ filters, onFiltersChange, filterOptions }: CompanyFiltersProps) {
  const [expanded, setExpanded] = useState(false)

  const update = useCallback(
    (patch: Partial<CompanyFilterParams>) => {
      onFiltersChange({ ...filters, ...patch })
    },
    [filters, onFiltersChange]
  )

  const activeCount = useMemo(() => {
    let count = 0
    if (filters.search) count++
    if (filters.entity_type) count++
    if (!filters.active_only) count++
    if (filters.okved) count++
    if (filters.region) count++
    if (filters.date_from) count++
    if (filters.date_to) count++
    if (filters.capital_min !== undefined) count++
    if (filters.revenue_min !== undefined) count++
    if (filters.revenue_max !== undefined) count++
    if (filters.profit_min !== undefined) count++
    if (filters.profit_max !== undefined) count++
    if (filters.assets_min !== undefined) count++
    if (filters.assets_max !== undefined) count++
    if (filters.taxes_min !== undefined) count++
    if (filters.taxes_max !== undefined) count++
    if (filters.has_phone) count++
    if (filters.has_email) count++
    if (filters.has_website) count++
    if (filters.has_licenses) count++
    if (filters.has_trademarks) count++
    if (filters.has_procurements) count++
    if (filters.director) count++
    return count
  }, [filters])

  const resetFilters = useCallback(() => {
    onFiltersChange({ ...defaultFilters })
  }, [onFiltersChange])

  const badgeList = useMemo(() => {
    const badges: { label: string; onRemove: () => void }[] = []
    if (filters.search) badges.push({ label: filters.search, onRemove: () => update({ search: undefined }) })
    if (filters.entity_type === 'organizations') badges.push({ label: 'Организации', onRemove: () => update({ entity_type: undefined }) })
    if (filters.entity_type === 'entrepreneurs') badges.push({ label: 'ИП', onRemove: () => update({ entity_type: undefined }) })
    if (!filters.active_only) badges.push({ label: 'Все статусы', onRemove: () => update({ active_only: true }) })
    if (filters.okved) badges.push({ label: filters.okved, onRemove: () => update({ okved: undefined }) })
    if (filters.region) badges.push({ label: filters.region, onRemove: () => update({ region: undefined }) })
    if (filters.date_from) badges.push({ label: `от ${filters.date_from}`, onRemove: () => update({ date_from: undefined }) })
    if (filters.date_to) badges.push({ label: `до ${filters.date_to}`, onRemove: () => update({ date_to: undefined }) })
    if (filters.director) badges.push({ label: filters.director, onRemove: () => update({ director: undefined }) })
    if (filters.has_phone) badges.push({ label: 'Телефон', onRemove: () => update({ has_phone: undefined }) })
    if (filters.has_email) badges.push({ label: 'Email', onRemove: () => update({ has_email: undefined }) })
    if (filters.has_website) badges.push({ label: 'Веб-сайт', onRemove: () => update({ has_website: undefined }) })
    if (filters.has_licenses) badges.push({ label: 'Лицензии', onRemove: () => update({ has_licenses: undefined }) })
    if (filters.has_trademarks) badges.push({ label: 'Товарные знаки', onRemove: () => update({ has_trademarks: undefined }) })
    if (filters.has_procurements) badges.push({ label: 'Госзакупки', onRemove: () => update({ has_procurements: undefined }) })
    return badges
  }, [filters, update])

  return (
    <div>
      {/* Top Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 10,
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 180 }}>
          <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            placeholder="Название, ИНН, ОГРН или ФИО..."
            value={filters.search ?? ''}
            onChange={e => update({ search: e.target.value || undefined })}
            style={{
              ...compactInputStyle,
              paddingLeft: 28,
              height: 34,
            }}
          />
        </div>

        {/* Entity type pills */}
        <div style={{ display: 'flex', gap: 0, borderRadius: 'var(--radius-xs)', overflow: 'hidden', border: '1px solid var(--border-default)' }}>
          {([
            { key: undefined as 'organizations' | 'entrepreneurs' | undefined, label: 'Все' },
            { key: 'organizations' as const, label: 'Организации' },
            { key: 'entrepreneurs' as const, label: 'ИП' },
          ] as const).map(opt => {
            const isAll = opt.key === undefined
            const isActive = isAll ? !filters.entity_type : filters.entity_type === opt.key
            return (
              <button
                key={String(opt.key ?? 'all')}
                onClick={() => update({ entity_type: isAll ? undefined : opt.key })}
                style={{
                  padding: '6px 10px',
                  fontSize: 11,
                  fontWeight: isActive ? 600 : 400,
                  background: isActive ? 'var(--accent-subtle)' : 'transparent',
                  color: isActive ? 'var(--accent-bright)' : 'var(--text-muted)',
                  border: 'none',
                  borderRight: opt.key !== 'entrepreneurs' ? '1px solid var(--border-subtle)' : 'none',
                  cursor: 'pointer',
                  transition: 'all var(--duration-fast)',
                  whiteSpace: 'nowrap' as const,
                }}
              >
                {opt.label}
              </button>
            )
          })}
        </div>

        {/* Active-only dot toggle */}
        <button
          onClick={() => update({ active_only: !filters.active_only })}
          title="Действующие"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '5px 10px',
            fontSize: 11,
            fontWeight: 500,
            background: 'transparent',
            border: `1px solid ${filters.active_only ? 'var(--success-border)' : 'var(--border-default)'}`,
            borderRadius: 'var(--radius-full)',
            color: filters.active_only ? 'var(--success)' : 'var(--text-muted)',
            cursor: 'pointer',
            transition: 'all var(--duration-fast)',
            whiteSpace: 'nowrap' as const,
          }}
        >
          <span style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: filters.active_only ? 'var(--success)' : 'var(--text-muted)',
            transition: 'background var(--duration-fast)',
          }} />
          Действующие
        </button>

        {/* Expand + Reset combined button */}
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '5px 10px',
            fontSize: 11,
            fontWeight: 500,
            background: 'var(--bg-input)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-xs)',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            transition: 'all var(--duration-fast)',
            whiteSpace: 'nowrap' as const,
          }}
        >
          <Filter size={13} />
          Фильтры
          {activeCount > 0 && (
            <span style={{
              background: 'var(--accent)',
              color: 'white',
              fontSize: 9,
              fontWeight: 700,
              borderRadius: 'var(--radius-full)',
              minWidth: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}>{activeCount}</span>
          )}
          {activeCount > 0 && (
            <span
              onClick={e => { e.stopPropagation(); resetFilters() }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '1' }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '0.6' }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: 2,
                cursor: 'pointer',
                opacity: 0.6,
                transition: 'opacity var(--duration-fast)',
              }}
            >
              <RotateCcw size={11} />
            </span>
          )}
        </button>
      </div>

      {/* Active filter badges */}
      {badgeList.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 4, marginBottom: 10 }}>
          {badgeList.map((b, i) => (
            <span key={i} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              padding: '2px 7px',
              fontSize: 10,
              fontWeight: 500,
              background: 'var(--accent-subtle)',
              border: '1px solid var(--accent-border)',
              borderRadius: 'var(--radius-full)',
              color: 'var(--accent-bright)',
              lineHeight: 1.4,
            }}>
              {b.label}
              <button
                onClick={b.onRemove}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 12,
                  height: 12,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  borderRadius: '50%',
                  lineHeight: 1,
                }}
              >
                <X size={8} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Collapsible filter sections */}
      {expanded && (
        <div className="card" style={{ padding: 0, marginBottom: 16, animation: 'fadeIn 0.2s ease' }}>
          <AccordionSection title="ОКВЭД" subtitle={filters.okved || ''} defaultOpen={!!filters.okved}>
            <OkvedAutocomplete
              value={filters.okved}
              onChange={v => update({ okved: v })}
              options={filterOptions?.main_okveds ?? []}
            />
          </AccordionSection>

          <AccordionSection title="Регион" defaultOpen={!!filters.region}>
            <input
              style={compactInputStyle}
              placeholder="Город, область или район..."
              value={filters.region ?? ''}
              onChange={e => update({ region: e.target.value || undefined })}
            />
          </AccordionSection>

          <AccordionSection title="Дата регистрации" defaultOpen={!!(filters.date_from || filters.date_to)}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <input
                type="date"
                style={compactInputStyle}
                value={filters.date_from ?? ''}
                onChange={e => update({ date_from: e.target.value || undefined })}
              />
              <input
                type="date"
                style={compactInputStyle}
                value={filters.date_to ?? ''}
                onChange={e => update({ date_to: e.target.value || undefined })}
              />
            </div>
          </AccordionSection>

          <AccordionSection title="Финансы" defaultOpen={!!(filters.capital_min || filters.revenue_min || filters.revenue_max || filters.profit_min || filters.profit_max)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <NumericInput placeholder="Уставный капитал от, ₽" value={filters.capital_min} onChange={v => update({ capital_min: v })} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <NumericInput placeholder="Выручка от, ₽" value={filters.revenue_min} onChange={v => update({ revenue_min: v })} />
                <NumericInput placeholder="Выручка до, ₽" value={filters.revenue_max} onChange={v => update({ revenue_max: v })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <NumericInput placeholder="Прибыль от, ₽" value={filters.profit_min} onChange={v => update({ profit_min: v })} />
                <NumericInput placeholder="Прибыль до, ₽" value={filters.profit_max} onChange={v => update({ profit_max: v })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <NumericInput placeholder="Активы от, ₽" value={filters.assets_min} onChange={v => update({ assets_min: v })} />
                <NumericInput placeholder="Активы до, ₽" value={filters.assets_max} onChange={v => update({ assets_max: v })} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <NumericInput placeholder="Налоги от, ₽" value={filters.taxes_min} onChange={v => update({ taxes_min: v })} />
                <NumericInput placeholder="Налоги до, ₽" value={filters.taxes_max} onChange={v => update({ taxes_max: v })} />
              </div>
            </div>
          </AccordionSection>

          <AccordionSection title="Директор" defaultOpen={!!filters.director}>
            <input
              style={compactInputStyle}
              placeholder="ФИО руководителя..."
              value={filters.director ?? ''}
              onChange={e => update({ director: e.target.value || undefined })}
            />
          </AccordionSection>

          <AccordionSection title="Наличие данных">
            <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 4 }}>
              <PillToggle label="Телефон" active={filters.has_phone} onChange={v => update({ has_phone: v })} />
              <PillToggle label="Email" active={filters.has_email} onChange={v => update({ has_email: v })} />
              <PillToggle label="Веб-сайт" active={filters.has_website} onChange={v => update({ has_website: v })} />
              <PillToggle label="Лицензии" active={filters.has_licenses} onChange={v => update({ has_licenses: v })} />
              <PillToggle label="Тов. знаки" active={filters.has_trademarks} onChange={v => update({ has_trademarks: v })} />
              <PillToggle label="Госзакупки" active={filters.has_procurements} onChange={v => update({ has_procurements: v })} />
            </div>
          </AccordionSection>
        </div>
      )}
    </div>
  )
}
